import React from 'react';
import { Alert, Form } from 'react-bootstrap';
import ApplicationSummary from './ApplicationSummary';

// Single source of truth for "what's missing from Review and Submit,"
// shared by CreateApplication.js's and ApplicationDetailPage.js's
// canProceed/error-summary — same pattern as
// CreateApplicationSection6.js's getGeneralAcknowledgmentErrors. idPrefix
// must match whatever the caller passes to the component below.
export function getReviewConfirmationErrors(
    confirmsSubmissionAccuracy,
    idPrefix
) {
    if (confirmsSubmissionAccuracy !== true) {
        return [
            {
                id: `${idPrefix}-confirms-submission-accuracy`,
                label: 'Submission accuracy confirmation',
            },
        ];
    }
    return [];
}

// Boolean form of getReviewConfirmationErrors, for callers that only need
// "is this complete," not the error detail — same pattern as
// CreateApplicationSection6's isGeneralAcknowledgmentComplete, including
// having no production caller today (kept for CreateApplication.test.js's
// dedicated unit tests). idPrefix is irrelevant to the boolean result, so
// this doesn't take one.
export function isReviewConfirmationComplete(confirmsSubmissionAccuracy) {
    return (
        getReviewConfirmationErrors(confirmsSubmissionAccuracy, 'ca').length ===
        0
    );
}

// Section 7: Review and Submit (Qualtrics Block 7) — the whole application,
// read-only (#477), plus a final confirmation checkbox.
//
// idPrefix keeps the checkbox's DOM id unique per caller (create wizard vs
// detail page), same reasoning as Section6's idPrefix.
function CreateApplicationSection7({
    application,
    confirmsSubmissionAccuracy,
    wasInvalidated,
    onChange,
    idPrefix,
    errorIds = new Set(),
    instructorMissing = false,
}) {
    const checkboxId = `${idPrefix}-confirms-submission-accuracy`;
    const errorTextId = `${checkboxId}-error`;
    const isInvalid = errorIds.has(checkboxId);
    // #455's hint needs the final program list. Section 7 renders last,
    // so application.programPreferences is settled by the time it does.
    const hasLA = (application?.programPreferences || []).some(
        (p) => p.programType === 'la'
    );

    return (
        <div>
            <h2 className="h5 mb-3">Review &amp; Submit</h2>
            <p className="text-muted mb-3">
                Confirm the details below are accurate before submitting.
            </p>
            {/* Last step, so it sees the final program list (#455) */}
            {hasLA && instructorMissing && (
                <Alert variant="info">
                    No instructor of record is named yet. You can still submit,
                    but LA can&apos;t award support until an instructor is
                    named.
                </Alert>
            )}
            <ApplicationSummary application={application} idPrefix={idPrefix} />
            {/* Always mounted (like the wizard footer's "Complete all
                required fields" live region) so screen readers announce
                the text as it appears, rather than needing to discover a
                freshly-mounted element on their own. */}
            <p className="text-danger small mb-2" aria-live="polite">
                {wasInvalidated &&
                    confirmsSubmissionAccuracy !== true &&
                    'Your confirmation was cleared because the application changed — please review the summary above again.'}
            </p>
            <Form.Check
                type="checkbox"
                id={checkboxId}
                label={
                    <>
                        I confirm the above is accurate to the best of my
                        knowledge. <span className="text-danger">*</span>
                    </>
                }
                checked={confirmsSubmissionAccuracy === true}
                onChange={(e) => onChange(e.target.checked)}
                required
                isInvalid={isInvalid}
                aria-invalid={isInvalid}
                aria-describedby={isInvalid ? errorTextId : undefined}
            />
            {isInvalid && (
                <Form.Control.Feedback
                    type="invalid"
                    id={errorTextId}
                    role="alert"
                    className="d-block"
                >
                    Confirmation is required.
                </Form.Control.Feedback>
            )}
        </div>
    );
}

export default CreateApplicationSection7;
